import { useEffect, useState } from 'react';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { InputField, TextareaField, SelectField } from '@/pages/dashboard/components/FormFields';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { uploadFile } from '@/lib/upload';

function ProfileEdit() {
  const { user, profile, refreshProfile } = useAuth();
  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    city: '',
    country: '',
    bio: '',
    sector: '',
  });
  const [sectors, setSectors] = useState<{ value: string; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        city: profile.city || '',
        country: profile.country || '',
        bio: profile.bio || '',
        sector: profile.sector || '',
      });
    }
  }, [profile]);

  useEffect(() => {
    let mounted = true;
    const loadSectors = async () => {
      const { data } = await supabase
        .from('categories')
        .select('name')
        .is('parent_id', null)
        .order('name');
      if (mounted && data) {
        setSectors(
          (data as { name: string }[]).map((c) => ({ value: c.name, label: c.name })),
        );
      }
    };
    loadSectors();
    return () => {
      mounted = false;
    };
  }, []);

  const updateField = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setStatus('idle');
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadingAvatar(true);
    setErrorMsg('');
    try {
      const url = await uploadFile(file, `avatars/${user.id}`);
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id);
      if (error) throw error;
      await refreshProfile();
      setStatus('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Échec de l'upload de l'avatar.");
      setStatus('error');
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setStatus('idle');
    setErrorMsg('');

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: form.full_name.trim() || null,
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
        country: form.country.trim() || null,
        bio: form.bio.trim() || null,
        sector: form.sector.trim() || null,
      })
      .eq('id', user.id);

    setSaving(false);

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    await refreshProfile();
    setStatus('success');
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50">Mon profil</h1>
        <p className="text-sm text-foreground-400 mt-1">
          Gérez les informations de votre compte personnel.
        </p>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="p-6 rounded-xl bg-background-100 border border-background-200/70 space-y-5">
          <div className="flex items-center gap-4 pb-5 border-b border-background-200/70">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-background-200/60 shrink-0 flex items-center justify-center">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-user-line text-3xl text-foreground-400" />
              )}
            </div>
            <div>
              <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-background-300/60 text-foreground-50 rounded-md hover:bg-background-200/60 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-upload-2-line" />
                {uploadingAvatar ? 'Envoi...' : 'Changer la photo'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={uploadingAvatar}
                />
              </label>
              <p className="text-xs text-foreground-500 mt-1">Format JPG ou PNG.</p>
            </div>
          </div>

          <InputField
            id="full_name"
            label="Nom complet"
            icon="ri-user-line"
            value={form.full_name}
            onChange={(v) => updateField('full_name', v)}
            placeholder="Jean Dupont"
          />

          <InputField
            id="email"
            label="Adresse email"
            icon="ri-mail-line"
            type="email"
            value={user?.email || ''}
            onChange={() => {}}
            disabled
            hint="L'adresse email ne peut pas être modifiée depuis cette page."
          />

          <InputField
            id="phone"
            label="Téléphone"
            icon="ri-phone-line"
            type="tel"
            value={form.phone}
            onChange={(v) => updateField('phone', v)}
            placeholder="+228 90 00 00 00"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              id="city"
              label="Ville"
              icon="ri-map-pin-line"
              value={form.city}
              onChange={(v) => updateField('city', v)}
              placeholder="Lomé"
            />
            <InputField
              id="country"
              label="Pays"
              icon="ri-earth-line"
              value={form.country}
              onChange={(v) => updateField('country', v)}
              placeholder="Togo"
            />
          </div>

          <SelectField
            id="sector"
            label="Secteur d'activité (domaine)"
            icon="ri-briefcase-line"
            value={form.sector}
            onChange={(v) => updateField('sector', v)}
            placeholder="Sélectionnez votre secteur"
            options={sectors}
          />

          <TextareaField
            id="bio"
            label="Bio"
            icon="ri-quill-pen-line"
            value={form.bio}
            onChange={(v) => updateField('bio', v)}
            placeholder="Présentez-vous en quelques mots : votre métier, votre parcours, ce que vous proposez..."
            rows={4}
            maxLength={500}
            hint="Cette bio apparaîtra sur votre profil public."
          />

          {status === 'error' && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
              {errorMsg}
            </div>
          )}
          {status === 'success' && (
            <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400">
              Profil mis à jour avec succès.
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default ProfileEdit;